import styles from './QuickEstimateCalculator.module.scss';
import { CircularActivityIndicator, Radio, RadioGroup, StandardButton, TextField, Dropdown, useWindowDimensions } from '@cxc/react-fds-components';
import React, { useEffect, useState } from 'react';
import MiniBolt from '../images/mini-bolt.svg';
import Result from './Result';
import useManualCalculator from '../../api/useManualCalculator';
import { ManualCalculatorRequest } from '../../models/ManualCalculatorRequest';
import { useAtom } from 'jotai';
import { aemContent } from '../../state/globalState';
import { htmlReactParserWrapper } from '../../utils/htmlReactParserWrapper';
import { constructDigitalData, fireAnalyticsEvent } from '../../analytics/analytics';
import { PAGE_NAME, GLOBAL_INTERACTION, GET_QUICK_ESTIMATE_SUBMIT_BUTTON_ONCLICKS, GET_QUICK_ESTIMATE_BUTTON_TOOL, GET_QUICK_ESTIMATE_BUTTON_TOOL_DESCRIPTOR, CALL_EMAIL, EMAIL_US_BUTTON_TOOL, EMAIL_US_BUTTON_TOOL_DESCRIPTOR, EMAIL_US_BUTTON_ONCLICKS } from '../../analytics/analyticsConstants';
import { FeatureText } from '@cxc/react-hydro-components';
import ReactHtmlParser from 'react-html-parser';
import { LocaleToConsultationLinksMap, marketConfiguration, LocalesConstants, QuickEstimateCalcConstants, URLConstants, AnalyticsConstants, SymbolConstants } from '../../utils/constants';
import environment from '../../environment';
import { useUniversalContext } from '@cxc/react-universal-components';
import getAllStates from '../../utils/getCountryStates';
import { layoutConstants } from '../../utils/breakPointConstant';

interface FuelType {
    key: string;
    label: string;
    checked: boolean;
    enum: string;
}

const QuickEstimateCalculator: React.FC = () => {
    const { width } = useWindowDimensions();
    const { language, isLoggedIn, subscribeScopedValue } = useUniversalContext();
    const [aem] = useAtom(aemContent);
    const [countryStatesList, setCountryStatesList] = useState<any>(undefined);
    const [loading, setLoading] = useState(false);
    const [calculateBatteryEnergy] = useManualCalculator();
    const [response, setResponse] = useState(null);
    const [, setError] = useState(null);
    const [selectedAreaCode, setSelectedAreaCode] = useState(null);
    const [vehicleMileageValidationError, setMileageErrorObj] = useState<any | null>(null);
    const [vehicleMileageValidationMessage, setMileageErrorMessage] = useState<any | null>('');
    const [dailyMileageValidationError, setDailyMileageErrorObj] = useState<any | null>(null);
    const [dailyMileageValidationMessage, setDailyMileageErrorMessage] = useState<any | null>('');
    const [dailyRuntimeValidationError, setAvgDailyRuntimeErrorObj] = useState<any | null>(null);
    const [dailyRuntimeValidationMessage, setAvgDailyRuntimeErrorMessage] = useState<any | null>('');
    const [stateError, setStateError] = useState<string | undefined>(undefined);
    const [vehicleMileage, setVehicleMileage] = useState<string | undefined>(undefined);
    const [avgDailyMileage, setAvgDailyMileage] = useState<string | undefined>(undefined);
    const [avgDailyRuntime, setAvgDailyRuntime] = useState<string | undefined>(undefined);
    const [isMobileFormSubmitted, setIsMobileFormSubmitted] = useState(false);

    const isMobile = width <= layoutConstants.CUSTOM_LAYOUT_MAX_TABLET ? true : false;
    const displayForm = !isMobile ? true : !isMobileFormSubmitted;

    const cookiePreferences = subscribeScopedValue(AnalyticsConstants.FPG_COOKIES, AnalyticsConstants.COOKIE_PREFERENCES);
    const mailToLink = aem?.['section-3']?.['props']?.['ctaUrl'] || null;
    const heading = htmlReactParserWrapper(aem?.['section-3']?.['props']?.['heading'] || null);
    const estimatorHeading = htmlReactParserWrapper(aem?.['section-2']?.['props']?.['heading']);
    const estimatorHeadingDescription = htmlReactParserWrapper(aem?.['section-2']?.['props']?.['text'] || null);
    const emailUsFeatureTextAem = aem?.['section-3']?.['props']?.['text'];
    const emailUsFeatureText = htmlReactParserWrapper(emailUsFeatureTextAem || null);
    const bookConsultationButtonLabel = htmlReactParserWrapper(aem?.['section-3']?.['props']?.['ctaText'] || null);
    const bookConsultationButtonAriaLabel = aem?.['section-3']?.['props']?.['ctaAriaLabel'] || undefined;
    const disclosureTitle = htmlReactParserWrapper(aem?.['section-3']?.['props']?.['popOverButtonText'] || null);
    const disclosureBody = <div>{ReactHtmlParser(aem?.['section-3']?.['props']?.['disclosures']?.[0]?.disclosureKey + '  ' + aem?.['section-3']?.['props']?.['disclosures']?.[0]?.disclosure?.['disclosureText'])}</div>;
    const title = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[0]?.['text'] || null);
    const radioButtonLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[0]?.['textTwo'] || null);
    const gasLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[1]?.['label'] || null);
    const dieselLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[2]?.['label'] || null);
    const vehicleMpgLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[3]?.['defaultValue'] || null);
    const avgDailyMileaLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[4]?.['defaultValue'] || null);
    const avgDailyRunTimeLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[5]?.['defaultValue'] || null);
    const resultsLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[8]?.['text'] || null);
    const transitVanImage = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[7] || null);
    const VEHICLE_MILEAGE_INPUT_ERROR = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[12]?.label || QuickEstimateCalcConstants.VEHICLE_MILEAGE_INPUT_ERROR);
    const VEHICLE_DAILY_MILEAGE_INPUT_ERROR = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[13]?.label || QuickEstimateCalcConstants.VEHICLE_DAILY_MILEAGE_INPUT_ERROR);
    const VEHICLE_DAILY_RUNTIME_INPUT_ERROR = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[14]?.label || QuickEstimateCalcConstants.VEHICLE_DAILY_RUNTIME_INPUT_ERROR);
    const showResultsButtonLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['ctaText'] || null);
    const showResultsButtonAriaLabel = aem?.['field-container-1']?.['props']?.['ctaAriaLabel'] || undefined;
    const loadingButtonLabel: any = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['ctaTextTwo'] || null);
    const loadingButtonAriaLabel = aem?.['field-container-1']?.['props']?.['ctaAriaLabelTwo'] || undefined;
    const resultsLinkLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[7]?.['heading'] || null);
    const editLinkLabel = htmlReactParserWrapper(aem?.['field-container-1']?.['props']?.['fieldReference']?.[9]?.['fieldReference']?.[8]?.['heading'] || null);

    const [fuelTypes, setFuelTypes] = useState<FuelType[]>([
        {
            key: 'gas',
            label: gasLabel,
            checked: true,
            enum: 'PETROL',
        },
        {
            key: 'diesel',
            label: dieselLabel,
            checked: false,
            enum: 'DIESEL',
        },
    ]);

    useEffect(() => {
        if (language === LocalesConstants.USLocale) {
            setCountryStatesList(getAllStates());
        } else {
            setSelectedAreaCode(marketConfiguration[language].xCountryCode);
        }
    }, [language]);

    let bookConsultationLink = environment.FORDPRO_URL + URLConstants.BOOK_CONSULTATION;
    if (LocaleToConsultationLinksMap[language]) {
        bookConsultationLink = LocaleToConsultationLinksMap[language];
        if (mailToLink) {
            bookConsultationLink = mailToLink;
        }
    }

    const isFormValid = () => {
        let isValid = true;
        if (countryStatesList && !selectedAreaCode) {
            setStateError(QuickEstimateCalcConstants.VEHICLE_STATE_SELECTION);
            isValid = false;
        }
        if (!vehicleMileage) {
            const errorObj = { error: VEHICLE_MILEAGE_INPUT_ERROR };
            setMileageErrorObj(errorObj);
            setMileageErrorMessage(VEHICLE_MILEAGE_INPUT_ERROR);
            isValid = false;
        }
        if (!avgDailyMileage) {
            const errorObj = { error: VEHICLE_DAILY_MILEAGE_INPUT_ERROR };
            setDailyMileageErrorObj(errorObj);
            setDailyMileageErrorMessage(VEHICLE_DAILY_MILEAGE_INPUT_ERROR);
            isValid = false;
        }
        if (!avgDailyRuntime) {
            const errorObj = { error: VEHICLE_DAILY_RUNTIME_INPUT_ERROR };
            setAvgDailyRuntimeErrorObj(errorObj);
            setAvgDailyRuntimeErrorMessage(VEHICLE_DAILY_RUNTIME_INPUT_ERROR);
            isValid = false;
        }

        return isValid;
    };

    const handleFormSubmit = async (e: any) => {
        e.preventDefault();
        if (isFormValid()) {
            const vehicleMpg: string = e.target.vehicleMpg?.value;
            const avgDailyMileage: string = e.target.avgDailyMileage?.value;
            const avgDailyVehicleRunTime: string = e.target.avgDailyVehicleRunTime?.value;
            setLoading(true);
            if (isMobile) {
                setIsMobileFormSubmitted(true);
            }
            const requestBody: ManualCalculatorRequest = {
                fuelVolumeUsedPerDay: {
                    approximation: {
                        milesPerGallon: vehicleMpg,
                        milesDriven: avgDailyMileage,
                    },
                },
                vehicleUsageTimePerDay: {
                    unit: 'HOURS',
                    value: avgDailyVehicleRunTime,
                },
                fuelType: fuelTypes.find((fuelType) => fuelType.checked === true)?.enum,
                batteryCapacityToCompare: [
                    {
                        unit: 'KWH',
                        value: marketConfiguration[language]['batteryCapacity4P'],
                    },
                ],
                areaCode: selectedAreaCode,
            };
            await calculateBatteryEnergy(setResponse, requestBody, setError);
            const digitalData = constructDigitalData('Show Results', PAGE_NAME, GLOBAL_INTERACTION, GET_QUICK_ESTIMATE_BUTTON_TOOL, GET_QUICK_ESTIMATE_BUTTON_TOOL_DESCRIPTOR, GET_QUICK_ESTIMATE_SUBMIT_BUTTON_ONCLICKS, isLoggedIn, cookiePreferences);
            fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
            setLoading(false);
        }
    };

    const handleStateSelection = (state: any): void => {
        setSelectedAreaCode(state.value);
        setStateError(undefined);
    };

    const handleVehicleMileageValidation = (event) => {
        let errorMessage = '';
        let errorObj = null;
        if (event.target.value.match('^[1-9][0-9]?$|^100$')) {
            setVehicleMileage(event.target.value);
        } else {
            setVehicleMileage(undefined);
            errorMessage = VEHICLE_MILEAGE_INPUT_ERROR;
            errorObj = { error: errorMessage };
        }

        setMileageErrorObj(errorObj);
        setMileageErrorMessage(errorMessage);
    };

    const handleAvgDailyMileageValidation = (event) => {
        let errorMessage = '';
        let errorObj = null;
        if (event.target.value > 0 && event.target.value <= 2000) {
            setAvgDailyMileage(event.target.value);
        } else {
            setAvgDailyMileage(undefined);
            errorMessage = VEHICLE_DAILY_MILEAGE_INPUT_ERROR;
            errorObj = { error: errorMessage };
        }
        setDailyMileageErrorObj(errorObj);
        setDailyMileageErrorMessage(errorMessage);
    };

    const handleAvgDailyRuntimeValidation = (event) => {
        let errorMessage = '';
        let errorObj = null;
        if (event.target.value.match('^1?[0-9]?$|^2[0-4]$')) {
            setAvgDailyRuntime(event.target.value);
        } else {
            setAvgDailyRuntime(undefined);
            errorMessage = VEHICLE_DAILY_RUNTIME_INPUT_ERROR;
            errorObj = { error: errorMessage };
        }

        setAvgDailyRuntimeErrorObj(errorObj);
        setAvgDailyRuntimeErrorMessage(errorMessage);
    };

    const handleFuelType = (selectedFuelType: string): void => {
        const updatedFuelTypes = fuelTypes.map((fuelType) => {
            return {
                ...fuelType,
                checked: fuelType.key === selectedFuelType,
            };
        });
        setFuelTypes(updatedFuelTypes);
    };

    const handleEmailUsAnalyticsEvent = () => {
        const digitalData = constructDigitalData('lead submitted, updates opt-In, tools', PAGE_NAME, CALL_EMAIL, EMAIL_US_BUTTON_TOOL, EMAIL_US_BUTTON_TOOL_DESCRIPTOR, EMAIL_US_BUTTON_ONCLICKS, isLoggedIn, cookiePreferences);
        fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
    };

    return (
        <div className="fds-layout-grid" id="quick-estimate-calculator" data-testid="quick-estimate-calculator">
            <div className="fds-layout-grid__inner" id={'vehicles-disclosure-text'}>
                <div className="fds-layout-grid__cell--span-12">
                    <FeatureText className={styles.h3} headingText={estimatorHeading} bodyText={estimatorHeadingDescription} headingTextHtmlTag="h3" type="headingLarge" />
                </div>
            </div>
            <div id="quick-abc" className={`fds-layout-grid__inner ${styles.wrapper}`}>
                {displayForm ? (
                    <div className={`fds-layout-grid__cell--span-5 ${styles.formGrid}`}>
                        <p className={styles.p}>{title}</p>
                        <div className={'fds-app'}>
                            <form onSubmit={handleFormSubmit} className="fmc-mt-5">
                                <div className="fds-layout-grid__cell--span-12">
                                    <RadioGroup horizontal legend={`${radioButtonLabel}`}>
                                        {fuelTypes &&
                                            fuelTypes.map((fuelType) => (
                                                <React.Fragment key={fuelType.key}>
                                                    <Radio checked={fuelType.checked} value={fuelType.key} onChange={(e) => handleFuelType(e)}>
                                                        {fuelType.label}
                                                    </Radio>
                                                </React.Fragment>
                                            ))}
                                    </RadioGroup>
                                </div>
                                <div className="fds-layout-grid__cell--span-12 fmc-mt-5">
                                    <TextField placeholder={`${vehicleMpgLabel}`} data-testid="vehicleMileage" name="vehicleMpg" type={'number'} onChange={handleVehicleMileageValidation} error={vehicleMileageValidationError} message={vehicleMileageValidationMessage} />
                                </div>

                                <div className=" fds-layout-grid__cell--span-12 fmc-mt-5">
                                    <TextField name="avgDailyMileage" data-testid="dailyMileage" placeholder={`${avgDailyMileaLabel}`} type={'number'} onChange={handleAvgDailyMileageValidation} error={dailyMileageValidationError} message={dailyMileageValidationMessage} />
                                </div>
                                <div className=" fds-layout-grid__cell--span-12 fmc-mt-5">
                                    <TextField name="avgDailyVehicleRunTime" data-testid="dailyRuntime" placeholder={`${avgDailyRunTimeLabel}`} type={'number'} onChange={handleAvgDailyRuntimeValidation} error={dailyRuntimeValidationError} message={dailyRuntimeValidationMessage} />
                                </div>

                                {countryStatesList && (
                                    <div className=" fds-layout-grid__cell--span-12 fmc-mt-5">
                                        <Dropdown data-testid="selectState" onChange={handleStateSelection} options={countryStatesList} placeholder="Choose your state" error={stateError} />
                                    </div>
                                )}
                                <div className="fds-layout-grid__cell--span-12 fmc-mt-5">
                                    <StandardButton icon={<img src={MiniBolt} />} iconAlignment="right" data-testid="showResultsButton" type="submit" ariaLabel={loading ? loadingButtonAriaLabel : showResultsButtonAriaLabel} title={loading ? loadingButtonLabel : showResultsButtonLabel} />
                                </div>
                            </form>
                        </div>
                    </div>
                ) : (
                    <div className="fds-layout-grid__cell--span-12">
                        <div className={styles.mobileHeading}>
                            <span id={styles.links}>
                                <span>{resultsLinkLabel}</span>
                                <span onClick={() => setIsMobileFormSubmitted(false)}>{editLinkLabel}</span>
                            </span>
                        </div>
                        <div className={styles.imageWrapper}>
                            {!loading && response ? (
                                <Result data={response} isMobile={isMobile} />
                            ) : loading ? (
                                <div className={styles.mrgTop25}>
                                    <CircularActivityIndicator />
                                </div>
                            ) : (
                                <>
                                    <img className={`${styles.reduceOpacity} ${styles.mrgTop25}`} src={environment.AEM_URL + transitVanImage} alt="transit" />
                                    {resultsLabel}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {!isMobile && (
                    <div className="fds-layout-grid__cell--span-7">
                        <div className={styles.imageWrapper}>
                            {!loading && response ? (
                                <Result data={response} isMobile={isMobile} />
                            ) : loading ? (
                                <div className={styles.mrgTop25}>
                                    <CircularActivityIndicator />
                                </div>
                            ) : (
                                <>
                                    <img className={`${styles.reduceOpacity} ${styles.mrgTop25}`} src={environment.AEM_URL + transitVanImage} alt="transit" />
                                    {resultsLabel}
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div id={'charge-network-disclosure-text'} className="fds-layout-grid__cell--span-12 fds-align--center">
                    <FeatureText className={styles.h3} bodyText={emailUsFeatureText} disclosureBody={disclosureBody} disclosureTitle={disclosureTitle} headingText={heading} headingTextHtmlTag="h3" type="headingLarge" />
                    <div className={styles.buttonWrapper}>
                        <StandardButton iconAlignment="right" type="submit" data-testid="book-consulation-btn" ariaLabel={bookConsultationButtonAriaLabel} title={bookConsultationButtonLabel} as="a" href={bookConsultationLink} target="_blank" onClick={handleEmailUsAnalyticsEvent} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuickEstimateCalculator;
